import React, { useCallback, useState, useEffect, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTracker } from "@dpdgroupuk/react-event-tracker";

import palette from "../../styles/palette";
import RightDrawerModal from "../../RightDrawerModal";
import ModalTable from "../../common/Table/ModalTable";
import {
  TRANSFER_STOP,
  TRANSFER_PARCELS,
  SELECTED_PARCELS,
  SELECTED_STOPS,
  DISMISS,
  UNABLE_TRANSFER_TO_LOCKED_ROUTE,
  TRANSFER_HIGH_VALUE_PARCEL,
  TRANSFER_AND_SCHEDULE,
  TRANSFER_AND_OPTIMISE,
  TRANSFER_ONLY,
} from "../../../constants/strings";
import SelectDriverRoute from "../DriverRequests/components/SelectDriverRoute";
import useLocationQueryParams from "../../../hooks/useLocationQueryParams";
import { getCurrentUserId } from "../../../store/selectors/user";
import { getAvailableRoutesTransfer } from "../../../store/actions/request";
import selectedStopsTableProps from "../../../features/transferTo/selectedStopsTableProps";
import selectedParcelsTableProps from "../../../features/transferTo/selectedParcelsTableProps";
import {
  headerCellStyles,
  bodyCellStyles,
} from "../../../features/request/tableStyles";
import { processTransfer } from "../../../store/actions/ui/routeDetail/transferTo";
import { showNotification } from "../../../store/actions/notifications";
import { NotificationType } from "../../../features/Notifier";
import { isRouteReadyOrOpen } from "../../../store/selectors/route";
import { isRouteLocked } from "./utils";
import {
  TRANSFER_MODAL,
  TRANSFER_STOP_MODAL,
} from "../../../constants/analytics";
import { useOverlay } from "../../Overlay/useOverlay";
import { hasHighValueParcel } from "../../../models/parcel";
import StaticBanner from "../../StaticBanner";
import TransferActionButtons from "./components/TransferActionButtons";
import { setScheduleRequired } from "../../../store/actions/route";
import {
  getUniqueRoutesFromSelectedParcels,
  getUniqueRoutesFromSelectedStops,
} from "../../../store/selectors/ui/routeDetail";

const TransferTo = ({ open, onClose, onConfirm, isStopsSelected }) => {
  const dispatch = useDispatch();
  const tracker = useTracker();
  const { showOverlayWhile } = useOverlay();

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routes, setRoutes] = useState([]);

  const query = useLocationQueryParams();
  const uid = useSelector(getCurrentUserId);
  const uniqueNextWaveRoutes = useSelector(
    isStopsSelected
      ? getUniqueRoutesFromSelectedStops
      : getUniqueRoutesFromSelectedParcels
  );
  const routeReadyOrOpen = useSelector(isRouteReadyOrOpen);
  const { depot, date } = query;

  const modalTitle = isStopsSelected ? TRANSFER_STOP : TRANSFER_PARCELS;
  const tableTitle = isStopsSelected ? SELECTED_STOPS : SELECTED_PARCELS;
  const isActionBtnDisabled =
    !selectedRoute || isRouteLocked(routes, selectedRoute);

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(depot, date, uid, uniqueNextWaveRoutes)
      );
      setRoutes(availableRoutes);
    };
    fetchRoutes();
  }, [date, depot, uid, isStopsSelected, dispatch, uniqueNextWaveRoutes]);

  const processTransferToRoute = useCallback(
    async (optimise, transferOnly = false) => {
      const data = await dispatch(
        processTransfer(isStopsSelected, selectedRoute)
      );
      if (data) {
        transferOnly && dispatch(setScheduleRequired(true));
        onConfirm({ optimise, transferOnly });
      }
    },
    [isStopsSelected, selectedRoute, onConfirm, dispatch]
  );

  const onSchedule = useCallback(async () => {
    tracker.logEvent(TRANSFER_STOP_MODAL.CLICK_SCHEDULE);
    await processTransferToRoute(false);
  }, [processTransferToRoute, tracker]);

  const onOptimise = useCallback(async () => {
    tracker.logEvent(TRANSFER_STOP_MODAL.CLICK_OPTIMISE);
    await processTransferToRoute(true);
  }, [processTransferToRoute, tracker]);

  const onTransferOnly = useCallback(async () => {
    // TODO add action ID
    await processTransferToRoute(false, true);
  }, [processTransferToRoute]);

  const getTableProps = useCallback(() => {
    const config = isStopsSelected
      ? selectedStopsTableProps
      : selectedParcelsTableProps;
    return config(headerCellStyles(), bodyCellStyles());
  }, [isStopsSelected]);

  const tableProps = getTableProps();

  const showHighValueMessage = useMemo(
    () => hasHighValueParcel(tableProps.data),
    [tableProps.data]
  );

  const onChange = useCallback(
    (value) => {
      if (isRouteLocked(routes, value)) {
        dispatch(
          showNotification({
            key: "transfer-locked-route-error",
            type: NotificationType.ACTION,
            message: UNABLE_TRANSFER_TO_LOCKED_ROUTE,
            buttonText: DISMISS,
          })
        );
      }

      setSelectedRoute(value);
      tracker.logEvent({ actionId: TRANSFER_MODAL.SELECT_ROUTE, value });
    },
    [routes, dispatch, tracker]
  );

  const transferButtonsConfig = useMemo(
    () => ({
      transfer: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.orange,
          border: palette.modalButtons.greenBorder,
        },
        title: TRANSFER_ONLY,
        onClick: showOverlayWhile(onTransferOnly),
      },
      optimise: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.green,
          border: palette.modalButtons.greenBorder,
        },
        title: TRANSFER_AND_OPTIMISE,
        onClick: showOverlayWhile(onOptimise),
      },
      schedule: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.blue,
          border: palette.btn.blueBorder,
        },
        title: TRANSFER_AND_SCHEDULE,
        onClick: showOverlayWhile(onSchedule),
      },
    }),
    [
      routeReadyOrOpen,
      isActionBtnDisabled,
      onOptimise,
      onSchedule,
      onTransferOnly,
      showOverlayWhile,
    ]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      loadId={TRANSFER_MODAL.LOAD}
      actions={<TransferActionButtons config={transferButtonsConfig} />}
    >
      {showHighValueMessage && (
        <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
      )}
      <ModalTable title={tableTitle} {...tableProps} />
      <SelectDriverRoute
        options={routes}
        onChange={onChange}
        value={selectedRoute}
      />
    </RightDrawerModal>
  );
};

export default memo(TransferTo);
